import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { insertLenderFeeGroupSchema, type LenderFeeGroup } from "@shared/schema";
import { z } from "zod";

type FeeGroupFormData = z.infer<typeof insertLenderFeeGroupSchema>;

interface FeeGroupFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lenderId: string;
  feeGroup?: LenderFeeGroup;
  onFeeGroupCreated?: (feeGroup: LenderFeeGroup) => void;
}

export function FeeGroupFormModal({
  open,
  onOpenChange,
  lenderId,
  feeGroup,
  onFeeGroupCreated,
}: FeeGroupFormModalProps) {
  const { toast } = useToast();
  const isEdit = !!feeGroup;

  const defaultValues: Partial<FeeGroupFormData> = {
    lenderId,
    groupName: "",
    description: "",
    isDefault: false,
  };

  const form = useForm<FeeGroupFormData>({
    resolver: zodResolver(insertLenderFeeGroupSchema),
    defaultValues,
    mode: "onChange",
    criteriaMode: "all",
  });

  useEffect(() => {
    if (open) {
      if (feeGroup) {
        // Only reset with form-relevant fields to avoid Zod strict mode validation errors
        // Database-only fields (id, createdAt, updatedAt, organisationId) cause silent validation failures
        form.reset({
          lenderId: feeGroup.lenderId,
          groupName: feeGroup.groupName,
          description: feeGroup.description,
          isDefault: feeGroup.isDefault,
        });
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, feeGroup, form]);

  const createMutation = useMutation({
    mutationFn: async (data: FeeGroupFormData) => {
      const response = await apiRequest("POST", `/api/crm/lenders/${lenderId}/fee-groups`, data);
      return response.json();
    },
    onSuccess: async (newGroup: LenderFeeGroup) => {
      // Use predicate to match ALL lender-related queries (fixes ISSUE-072)
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.some((key) =>
            typeof key === "string" && key.includes("/api/crm/lenders")
          ),
      });
      toast({
        title: "Fee group created",
        description: "The fee group has been created successfully.",
      });
      onOpenChange(false);
      if (onFeeGroupCreated) onFeeGroupCreated(newGroup);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: FeeGroupFormData) =>
      apiRequest("PATCH", `/api/crm/fee-groups/${feeGroup?.id}`, data),
    onSuccess: () => {
      // Use predicate to match ALL lender-related queries (fixes ISSUE-072)
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.some((key) =>
            typeof key === "string" && key.includes("/api/crm/lenders")
          ),
      });
      toast({
        title: "Fee group updated",
        description: "The fee group has been updated successfully.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FeeGroupFormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Fee Group" : "Add Fee Group"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update fee group information" : "Create a new fee group template"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="groupName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Group Name *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Standard Application Fees"
                      data-testid="input-group-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ""}
                      placeholder="Standard fees for all applications..."
                      data-testid="input-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value || false}
                      onCheckedChange={field.onChange}
                      data-testid="checkbox-is-default"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Default fee group</FormLabel>
                    <FormDescription>
                      Set as default fee group for this lender
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending} data-testid="button-save">
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEdit ? "Update" : "Create"} Fee Group
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
